import { Backdrop, CircularProgress } from '@mui/material';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { ErrorDialog } from '../components/error-dialog';
import { ErrorCode } from '../enums/ErrorCode';
import { SearchError } from '../enums/searchError';
import { EditionModel, LanguageModel, MarketModel } from '../models';
import { IUrlParameters } from '../models/urlParameters';
import ServicesContext, { ServicesCollection } from './servicesContext';

export interface ICarSelectorAppContext {
    edition: string;
    editions: EditionModel[];
    market: string;
    markets: MarketModel[];
    language: string;
    languages: LanguageModel[];
    hasError: boolean;
    setError: (titleKey: string, descriptionKey: string) => void;
    getEditions: (carModel: string) => Promise<EditionModel[]>;
    getMarkets: (carModel: string, edition: string) => Promise<MarketModel[]>;
    getLanguages: (carModel: string, edition: string, market: string) => Promise<LanguageModel[]>;
    setSearchData: (edition: string, market: string, language: string, hasDigitalManuals: boolean) => void;
    modelType: string;
    initData: (urlParameters: IUrlParameters, callbackHandler: () => void) => void;
    hasProductionDigitalManuals: boolean;
    urlParameters: IUrlParameters;
    clearUrlParameters: () => void;
    setLoading: (isLoading: boolean) => void,
    services: ServicesCollection
}

const defaultContext: ICarSelectorAppContext = {
    edition: null,
    editions: [],
    market: null,
    markets: [],
    language: null,
    languages: [],
    hasError: false,
    setError: (titleKey: string, descriptionKey: string) => Function(),
    getEditions: (carModel: string) => new Promise((resolve, reject) => resolve([])),
    getMarkets: (carModel: string, edition: string) => new Promise((resolve, reject) => resolve([])),
    getLanguages: (carModel: string, edition: string, market: string) => new Promise((resolve, reject) => resolve([])),
    setSearchData: (edition: string, market: string, language: string, hasDigitalManuals: boolean) => Function(),
    modelType: null,
    initData: (urlParameters: IUrlParameters, callbackHandler: () => void) => Function(),
    hasProductionDigitalManuals: false,
    urlParameters: null,
    clearUrlParameters: () => Function(),
    setLoading: (isLoading: boolean) => Function(),
    services: null
}

export const CarSelectorAppContext: React.Context<ICarSelectorAppContext> = React.createContext<ICarSelectorAppContext>(defaultContext);

interface ICarSelectorContextProps extends Partial<RouteComponentProps<any>> {}

interface ICarSelectorContextState {
    hasError: boolean;
    errorTitle: string;
    errorDescription: string;
    edition: string;
    editions: EditionModel[];
    market: string;
    markets: MarketModel[];
    language: string;
    languages: LanguageModel[];
    modelType: string;
    hasProductionDigitalManuals: boolean;
    urlParameters: IUrlParameters;
    isLoading: boolean
}

class CarSelectorContext extends React.Component<Partial<ICarSelectorContextProps>, ICarSelectorContextState> {

    static contextType = ServicesContext;
    context: React.ContextType<typeof ServicesContext>;

    constructor(props: any) {
        super(props);

        this.state = {
            hasError: false,
            errorTitle: "",
            errorDescription: "",
            editions: [],
            languages: [],
            markets: [],
            edition: null,
            language: null,
            market: null,
            modelType: null,
            hasProductionDigitalManuals: false,
            urlParameters: null,
            isLoading: false
        };
    }

    render(): any {
        return <CarSelectorAppContext.Provider value={{
            hasError: this.state.hasError,
            setError: (titleKey: string, descriptionKey: string) => this.setState({ ...this.state, hasError: true, errorTitle: titleKey, errorDescription: descriptionKey }),
            getEditions: this.getEditions,
            getMarkets: this.getMarkets,
            getLanguages: this.getLanguages,
            editions: this.state.editions,
            markets: this.state.markets,
            languages: this.state.languages,
            setSearchData: (edition: string, market: string, language: string, hasDigitalManuals: boolean) =>
                this.setState({ ...this.state, edition: edition, market: market, language: language, hasProductionDigitalManuals: hasDigitalManuals, urlParameters: null }),
            edition: this.state.edition,
            market: this.state.market,
            language: this.state.language,
            modelType: this.state.modelType,
            initData: this.initData,
            hasProductionDigitalManuals: this.state.hasProductionDigitalManuals,
            urlParameters: this.state.urlParameters,
            clearUrlParameters: () => this.setState({ ...this.state, urlParameters: null }),
            setLoading: (isLoading: boolean) => this.setState({ ...this.state, isLoading: isLoading }),
            services: this.context
        }}>
            <React.Fragment>
                <Backdrop open={this.state.isLoading} style={{zIndex: 10002}}>
                    <CircularProgress color="inherit" />
                </Backdrop>
                {
                    this.state.hasError &&
                    <ErrorDialog
                        headingText={this.context.resourceProvider.getText(this.state.errorTitle)}
                        descriptionText={this.state.errorDescription ? this.context.resourceProvider.getText(this.state.errorDescription) : null}
                        onClose={() => this.setState({ ...this.state, hasError: false, errorTitle: "", errorDescription: "" })}
                    />
                }
                {this.props.children}
            </React.Fragment>
        </CarSelectorAppContext.Provider>    
    }

    showError = () => {
        this.setState({ ...this.state, hasError: true, errorTitle: 'Search_Error_Title', errorDescription: 'Search_Error_Description' });
    }

    initData = (urlParameters: IUrlParameters, callbackHandler: () => void) => {
        if (!urlParameters.vin) {
            this.setState({
                ...this.state,
                urlParameters: urlParameters,
                edition: urlParameters.edition,
                market: urlParameters.market,
                language: urlParameters.manualLang,
                modelType: urlParameters.model
            }, callbackHandler);
        }
    }

    getEditions = async (carModelType: string): Promise<EditionModel[]> => {
        if (carModelType == this.state.modelType && this.state.editions && this.state.editions.length > 0) {
            return this.state.editions;
		}

        this.setState({
            ...this.state,
            modelType: carModelType,
            edition: carModelType != this.state.modelType ? null : this.state.edition,
            market: carModelType != this.state.modelType ? null : this.state.market,
            language: carModelType != this.state.modelType ? null : this.state.language,
        });

        try {
            const editionsResult = await this.context.apiService.getEditions(carModelType);
            if (editionsResult.errorCode == ErrorCode.NoError) {
                this.setState({
                    ...this.state,
                    editions: editionsResult.results,
                    markets: [],
                    languages: [],
                });

                return editionsResult.results;
            }

            return [];
        }
        catch(error) {
            if (error == SearchError.RequestError) {
                this.showError();
            }
            throw error;
		}
    }

    getMarkets = async (carModel: string, edition: string): Promise<MarketModel[]> => {
        if (carModel == this.state.modelType &&
            edition == this.state.edition && this.state.editions && this.state.editions.length > 0 &&
            this.state.markets && this.state.markets.length > 0) {
            return this.state.markets;
        }
        this.setState({ ...this.state, edition: edition });
        try {
            const marketsResult = await this.context.apiService.getMarkets(carModel, edition);
            if (marketsResult.errorCode == ErrorCode.NoError) {
                this.setState({
                    ...this.state,
                    markets: marketsResult.results,
                    languages: [],
                });

                return marketsResult.results;
            }
            return [];
        }
        catch (error) {
            if (error == SearchError.RequestError) {
                this.showError();
            }
            throw error;
		}
    }

    getLanguages = async (carModel: string, edition: string, market: string): Promise<LanguageModel[]> => {
        var currentEdition = this.state.editions.find(x => x.code == edition || x.omsCode == edition);
        if (carModel == this.state.modelType &&
            edition == this.state.edition && this.state.editions && this.state.editions.length > 0 &&
            ((market && this.state.market && market == this.state.market && this.state.markets && this.state.markets.length > 0) ||
                !currentEdition.hasDigitalManuals) &&
            this.state.languages && this.state.languages.length) {
            return this.state.languages;
        }
        this.setState({ ...this.state, edition: edition, market: market });
        try {
            const languagesResult = await this.context.apiService.getLanguages(carModel, edition, market);
            if (languagesResult.errorCode == ErrorCode.NoError) {
                this.setState({
                    ...this.state,
                    languages: languagesResult.results,
                });

                return languagesResult.results;
            }
            return [];
        }
        catch (error) {
            if (error == SearchError.RequestError) {
                this.showError();
            }
            throw error;
		}
    }
}

export default CarSelectorContext;